/**
 * Types du tableau de bord.
 *
 * Le tableau de bord ne possède presque rien : il compose ce que les autres
 * domaines savent déjà calculer. Ce qui lui appartient en propre, ce sont les
 * agrégats par période — chiffre d'affaires encaissé, marge moyenne — et la
 * conformité e-facturation.
 */
import type { InvoiceSummary } from "../invoices/types.js";
import type { ProjectSummary } from "../projects/types.js";
import type { QuoteSummary } from "../quotes/types.js";

export interface PeriodComparison {
  current: number;
  previous: number;
  /** Variation en points ou en pourcentage selon la nature de la mesure. */
  change: number | null;
}

export interface DashboardKpis {
  /** Encaissé sur le mois en cours, comparé au mois précédent. */
  revenue: PeriodComparison;
  /** Marge moyenne des devis émis dans le mois, en points. */
  quoteMargin: PeriodComparison;
  pendingQuotes: number;
  activeProjects: number;
  /** Reste dû, toutes factures non soldées confondues. */
  totalDue: number;
  /** Part échue. C'est le chiffre qui doit faire réagir. */
  overdueDue: number;
  overdueCount: number;
  /**
   * Part du reste dû portée par des factures jamais envoyées, et part de
   * l'échu qu'elles représentent. Voir `Receivables` : l'encours et le circuit
   * de relance ne comptent pas la même chose, et c'est ce qui le dit.
   */
  unsentDue: number;
  unsentCount: number;
  unsentOverdueDue: number;
  einvoiceCompliancePercent: number | null;
}

/** Ce que l'agent renvoie quand on lui demande « où on en est ». */
export interface DaySummary {
  kpis: DashboardKpis;
  pendingQuotes: QuoteSummary[];
  driftingProjects: ProjectSummary[];
  overdueInvoices: InvoiceSummary[];
}

/**
 * Variation entre deux périodes.
 *
 * Renvoie null quand la période précédente est nulle : afficher « +100 % »
 * sur un premier mois d'activité ne veut rien dire, et « +∞ » encore moins.
 */
export function computeChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}
